import { CheckoutReturnType } from './../../constants/checkout-return-type';
import { ProcessId } from './../../services/constants.service';
import { Injectable } from '@angular/core';
import { AppState, createAppState } from './app.model';
import { AppStore } from './app.store';
import { AppQuery } from './app.query';
import { SessionStorageType } from '../../services/tracking/session-storage';
import { ICreatePaymentResponse } from '@legalzoom/business-formation-sdk';
import { ICreateCrossSellOrderResponse, IGetOrderContactResponse } from '@legalzoom/order-sdk';
import { GetCartBalanceByCartIdResponse, IUserCart } from '@legalzoom/cart-sdk';
import { IUpdateProcessingOrderResponse, IProcessingOrderQueueResponse } from '@legalzoom/processing-order-sdk';
import { Payment } from '@legalzoom/sdk-checkout';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private appQuery: AppQuery = new AppQuery(this.appStore);
  constructor(
    private appStore: AppStore
  ) {
    const storage = sessionStorage.getItem(SessionStorageType.APP);
    if (storage === undefined || storage === null) {
      const app: AppState = createAppState({});
      this.app = app;
    } else {
      this.app = JSON.parse(storage);
    }
  }

  public set app(app: AppState) {
    this.appStore.set([app]);
    sessionStorage.setItem(SessionStorageType.APP, JSON.stringify(app));
  }

  public get app(): AppState {
    if (this.appQuery.getCount() === 1) {
      const app = this.appQuery.getAll()[0];
      return createAppState({
        processId: ProcessId.LLC,
        accessToken: app.accessToken,
        sessionId: app.sessionId,
        customerId: app.customerId,
        loginEmail: app.loginEmail,
        questionnaireId: app.questionnaireId,
        orderId: app.orderId,
        cartId: app.cartId,
        processingOrderId: app.processingOrderId,
        isQuestionnaireComplete: app.isQuestionnaireComplete,
        currentView: app.currentView,
        amount: app.amount,
        paymentProfileId: app.paymentProfileId,
        paymentGateway: app.paymentGateway,
        getCartBalanceByCartIdResponse: app.getCartBalanceByCartIdResponse,
        gSuiteCrossSellOrderResponse: app.gSuiteCrossSellOrderResponse,
        getOrderContactResponse: app.getOrderContactResponse,
        gSuiteCrossSellPaymentResponse: app.gSuiteCrossSellPaymentResponse,
        updateProcessingOrderResponse: app.updateProcessingOrderResponse,
        expressOrderProcessType: app.expressOrderProcessType,
        tmsCrossSellOrderResponse: app.tmsCrossSellOrderResponse,
        lwtCrossSellOrderResponse: app.lwtCrossSellOrderResponse,
        tmsCrossSellPaymentResponse: app.tmsCrossSellPaymentResponse,
        lwtCrossSellPaymentResponse: app.lwtCrossSellPaymentResponse,
        createProcessingOrderQueue: app.createProcessingOrderQueue,
        isGuestConvertedOnCheckout: app.isGuestConvertedOnCheckout,
        firstName: app.firstName,
        lastName: app.lastName,
        cart: app.cart,
        payment: app.payment
      });
    } else {
      const ls = JSON.parse(sessionStorage.getItem(SessionStorageType.APP));
      if (ls === undefined) {
        return null;
      } else {
        return ls;
      }
    }
  }
  private set state(app: AppState) {
    this.appStore.set([app]);
    sessionStorage.setItem(SessionStorageType.APP, JSON.stringify(app));
  }

  private get state(): AppState {
    if (this.appQuery.getCount() === 1) {
      const app = this.appQuery.getAll()[0];
      return createAppState({
        processId: ProcessId.LLC,
        accessToken: app.accessToken,
        sessionId: app.sessionId,
        customerId: app.customerId,
        loginEmail: app.loginEmail,
        questionnaireId: 27,
        orderId: app.orderId,
        cartId: app.cartId,
        processingOrderId: app.processingOrderId,
        isQuestionnaireComplete: app.isQuestionnaireComplete,
        currentView: app.currentView,
        amount: app.amount,
        paymentProfileId: app.paymentProfileId,
        paymentGateway: app.paymentGateway,
        getCartBalanceByCartIdResponse: app.getCartBalanceByCartIdResponse,
        gSuiteCrossSellOrderResponse: app.gSuiteCrossSellOrderResponse,
        getOrderContactResponse: app.getOrderContactResponse,
        gSuiteCrossSellPaymentResponse: app.gSuiteCrossSellPaymentResponse,
        expressOrderProcessType: app.expressOrderProcessType,
        updateProcessingOrderResponse: app.updateProcessingOrderResponse,
        tmsCrossSellOrderResponse: app.tmsCrossSellOrderResponse,
        lwtCrossSellOrderResponse: app.lwtCrossSellOrderResponse,
        tmsCrossSellPaymentResponse: app.tmsCrossSellPaymentResponse,
        lwtCrossSellPaymentResponse: app.lwtCrossSellPaymentResponse,
        createProcessingOrderQueue: app.createProcessingOrderQueue,
        isGuestConvertedOnCheckout: app.isGuestConvertedOnCheckout,
        firstName: app.firstName,
        lastName: app.lastName,
        cart: app.cart,
        payment: app.payment
      });
    } else {
      const ls = JSON.parse(sessionStorage.getItem(SessionStorageType.APP));
      if (ls === undefined) {
        return null;
      } else {
        return ls;
      }
    }
  }

  public set accessToken(accessToken: string) {
    const state = this.state;
    state.accessToken = accessToken;
    this.state = state;
  }
  public get accessToken(): string {
    return this.state.accessToken;
  }
  public set sessionId(sessionId: string) {
    const state = this.state;
    state.sessionId = sessionId;
    this.state = state;
  }
  public get sessionId(): string {
    return this.state.sessionId;
  }
  public set customerId(customerId: number) {
    const state = this.state;
    state.customerId = customerId;
    this.state = state;
  }
  public get customerId(): number {
    return this.state.customerId;
  }
  public set loginEmail(loginEmail: string) {
    const state = this.state;
    state.loginEmail = loginEmail;
    this.state = state;
  }
  public get loginEmail(): string {
    return this.state.loginEmail;
  }

  public set cartId(cartId: number) {
    const state = this.state;
    state.cartId = cartId;
    this.state = state;
  }
  public get cartId(): number {
    return this.state.cartId;
  }

  public set orderId(orderId: number) {
    const state = this.state;
    state.orderId = orderId;
    this.state = state;
  }
  public get orderId(): number {
    return this.state.orderId;
  }

  public set processingOrderId(processingOrderId: number) {
    const state = this.state;
    state.processingOrderId = processingOrderId;
    this.state = state;
  }
  public get processingOrderId(): number {
    return this.state.processingOrderId;
  }
  public set questionnaireId(questionnaireId: number) {
    const state = this.state;
    state.questionnaireId = questionnaireId;
    this.state = state;
  }
  public get questionnaireId(): number {
    return this.state.questionnaireId;
  }
  public set processId(processId: number) {
    const state = this.state;
    state.processId = processId;
    this.state = state;
  }
  public get processId(): number {
    return this.state.processId;
  }
  public set currentView(currentView: string) {
    const state = this.state;
    state.currentView = currentView;
    this.state = state;
  }
  public get currentView(): string {
    return this.state.currentView;
  }
  public set amount(amount: number) {
    const state = this.state;
    state.amount = amount;
    this.state = state;
  }
  public get amount(): number {
    return this.state.amount;
  }

  public set paymentProfileId(paymentProfileId: number) {
    const state = this.state;
    state.paymentProfileId = paymentProfileId;
    this.state = state;
  }
  public get paymentProfileId(): number {
    return this.state.paymentProfileId;
  }
  public set paymentGateway(paymentGateway: string) {
    const state = this.state;
    state.paymentGateway = paymentGateway;
    this.state = state;
  }
  public get paymentGateway(): string {
    return this.state.paymentGateway;
  }
  public set getCartBalanceByCartIdResponse(getCartBalanceByCartIdResponse: GetCartBalanceByCartIdResponse) {
    const state = this.state;
    state.getCartBalanceByCartIdResponse = getCartBalanceByCartIdResponse;
    this.state = state;
  }
  public get getCartBalanceByCartIdResponse(): GetCartBalanceByCartIdResponse {
    return this.state.getCartBalanceByCartIdResponse;
  }

  public set gSuiteCrossSellOrderResponse(gSuiteCrossSellOrderResponse: ICreateCrossSellOrderResponse) {
    const state = this.state;
    state.gSuiteCrossSellOrderResponse = gSuiteCrossSellOrderResponse;
    this.state = state;
  }
  public get gSuiteCrossSellOrderResponse(): ICreateCrossSellOrderResponse {
    return this.state.gSuiteCrossSellOrderResponse;
  }

  public set getOrderContactResponse(getOrderContactResponse: IGetOrderContactResponse) {
    const state = this.state;
    state.getOrderContactResponse = getOrderContactResponse;
    this.state = state;
  }
  public get getOrderContactResponse(): IGetOrderContactResponse {
    return this.state.getOrderContactResponse;
  }

  public set gSuiteCrossSellPaymentResponse(gSuiteCrossSellPaymentResponse: ICreatePaymentResponse) {
    const state = this.state;
    state.gSuiteCrossSellPaymentResponse = gSuiteCrossSellPaymentResponse;
    this.state = state;
  }
  public get gSuiteCrossSellPaymentResponse(): ICreatePaymentResponse {
    return this.state.gSuiteCrossSellPaymentResponse;
  }
  public set updateProcessingOrderResponse(updateProcessingOrderResponse: IUpdateProcessingOrderResponse) {
    const state = this.state;
    state.updateProcessingOrderResponse = updateProcessingOrderResponse;
    this.state = state;
  }
  public get updateProcessingOrderResponse(): IUpdateProcessingOrderResponse {
    return this.state.updateProcessingOrderResponse;
  }
  public set expressOrderProcessType(expressOrderProcessType: CheckoutReturnType) {
    const state = this.state;
    state.expressOrderProcessType = expressOrderProcessType;
    this.state = state;
  }
  public get expressOrderProcessType(): CheckoutReturnType {
    return this.state.expressOrderProcessType;
  }
  public set tmsCrossSellOrderResponse(tmsCrossSellOrderResponse: ICreateCrossSellOrderResponse) {
    const state = this.state;
    state.tmsCrossSellOrderResponse = tmsCrossSellOrderResponse;
    this.state = state;
  }
  public get tmsCrossSellOrderResponse(): ICreateCrossSellOrderResponse {
    return this.state.tmsCrossSellOrderResponse;
  }
  public set lwtCrossSellOrderResponse(lwtCrossSellOrderResponse: ICreateCrossSellOrderResponse) {
    const state = this.state;
    state.lwtCrossSellOrderResponse = lwtCrossSellOrderResponse;
    this.state = state;
  }
  public get lwtCrossSellOrderResponse(): ICreateCrossSellOrderResponse {
    return this.state.lwtCrossSellOrderResponse;
  }
  public set tmsCrossSellPaymentResponse(tmsCrossSellPaymentResponse: ICreatePaymentResponse) {
    const state = this.state;
    state.tmsCrossSellPaymentResponse = tmsCrossSellPaymentResponse;
    this.state = state;
  }
  public get tmsCrossSellPaymentResponse(): ICreatePaymentResponse {
    return this.state.tmsCrossSellPaymentResponse;
  }
  public set lwtCrossSellPaymentResponse(lwtCrossSellPaymentResponse: ICreatePaymentResponse) {
    const state = this.state;
    state.lwtCrossSellPaymentResponse = lwtCrossSellPaymentResponse;
    this.state = state;
  }
  public get lwtCrossSellPaymentResponse(): ICreatePaymentResponse {
    return this.state.lwtCrossSellPaymentResponse;
  }
  public set createProcessingOrderQueue(createProcessingOrderQueue: IProcessingOrderQueueResponse) {
    const state = this.state;
    state.createProcessingOrderQueue = createProcessingOrderQueue;
    this.state = state;
  }
  public get createProcessingOrderQueue(): IProcessingOrderQueueResponse {
    return this.state.createProcessingOrderQueue;
  }
  public set isGuestConvertedOnCheckout(isGuestConvertedOnCheckout: boolean) {
    const state = this.state;
    state.isGuestConvertedOnCheckout = isGuestConvertedOnCheckout;
    this.state = state;
  }
  public get isGuestConvertedOnCheckout(): boolean {
    return this.state.isGuestConvertedOnCheckout;
  }
  public set firstName(firstName: string) {
    const state = this.state;
    state.firstName = firstName;
    this.state = state;
  }
  public get firstName(): string {
    return this.state.firstName;
  }
  public set lastName(lastName: string) {
    const state = this.state;
    state.lastName = lastName;
    this.state = state;
  }
  public get lastName(): string {
    return this.state.lastName;
  }
  public set cart(cart: IUserCart) {
    const state = this.state;
    state.cart = cart;
    this.state = state;
  }
  public get cart(): IUserCart {
    return this.state.cart;
  }
  public set payment(payment: Payment) {
    const state = this.state;
    state.payment = payment;
    this.state = state;
  }
  public get payment(): Payment {
    return this.state.payment;
  }
}
